// PLAT-3133 — joint account holder names.
//
// Socure verifies ONE named individual against the account. A holder field carrying two people
// ("John & Jane Smith") matches neither of them cleanly, so the verification comes back weaker
// than the data deserved — and nobody is told why. This spots the shapes that happen and says so.
//
// Neither finding blocks. A joint account is a real thing to have, and the participant may be
// typing exactly what is on their statement; the point is to tell them which name we verify
// against, not to stop them. Both findings are advisory, and submission is never gated on them.

/** Nothing suspicious, a soft advisory, or a confident one. Never a block. */
export type JointNameSeverity = 'none' | 'warning' | 'flag';

export interface JointNameFinding {
    severity: JointNameSeverity;
    /** Inline copy for the field, or null when there is nothing to say. */
    message: string | null;
}

const NONE: JointNameFinding = { severity: 'none', message: null };

/**
 * Characters that only ever join two parties in a personal name. A hyphen is deliberately absent:
 * "Smith-Jones" is one double-barrelled surname, not two people.
 */
const DELIMITERS = ['&', '+', '/', ','];

const CONJUNCTIONS = ['and', 'or'];

/**
 * How much of a name has to sit either side of an embedded "and"/"or" before the run-together
 * reading beats the ordinary-name reading.
 *
 * This number is the whole reason the concatenated case is a soft warning rather than a flag.
 * Alex-and-er, Am-and-a, Or-lando, D-or-is, Hern-and-ez, S-and-oval and Fern-and-ez all contain a
 * conjunction's letters and are all one person, so a bare substring search is useless here. What
 * separates "JohnandJane" from "Alexander" is that both halves of the former are name-sized. Three
 * characters clears every example above while still catching two short names run together
 * ("BobOrSue"). It cannot be exact — "Alexanderson" trips it — which is precisely why this
 * produces the soft finding and the delimiters produce the confident one.
 */
const MIN_NAME_PART_LENGTH = 3;

export const useJointHolderName = () => {

    /**
     * The name's letter-runs, lowercased. Splitting on every non-alphanumeric — not just
     * whitespace — is what keeps the run-together test honest: a hyphen or apostrophe separates
     * name parts just as a space does, so "Alexander-Smith" has to read as two runs. Left whole it
     * would look like a name-sized run either side of "and" and trip the warning.
     */
    const tokenize = (val: string): string[] =>
        (val ?? '')
            .toLowerCase()
            .split(/[^a-z0-9]+/)
            .filter(token => token.length > 0);

    /**
     * A conjunction standing on its own as a word — "John and Jane", "Bob or Sue". Whitespace is
     * what makes this safe: "and" as a whole token cannot be part of Alexander.
     */
    const hasStandaloneConjunction = (val: string): boolean =>
        tokenize(val).some(token => CONJUNCTIONS.includes(token));

    const hasDelimiter = (val: string): boolean =>
        DELIMITERS.some(delimiter => (val ?? '').includes(delimiter));

    /**
     * A conjunction buried inside a single word with a name-sized run on both sides —
     * "JohnandJane". See {@link MIN_NAME_PART_LENGTH} for why the length test is doing the work.
     */
    const hasRunTogetherConjunction = (val: string): boolean =>
        tokenize(val).some(token =>
            CONJUNCTIONS.some(conjunction => {
                // Every occurrence is considered: the first may be too close to an edge while a
                // later one sits squarely in the middle.
                let at = token.indexOf(conjunction);
                while (at !== -1) {
                    const before = at;
                    const after = token.length - (at + conjunction.length);
                    if (before >= MIN_NAME_PART_LENGTH && after >= MIN_NAME_PART_LENGTH) return true;
                    at = token.indexOf(conjunction, at + 1);
                }
                return false;
            }),
        );

    /**
     * What to say about a holder name, if anything.
     *
     * Business-titled accounts must not be passed here at all — "Smith & Sons LLC" is one legal
     * entity and its ampersand means nothing of the kind this looks for. Callers gate on the
     * account holder type rather than this function second-guessing them.
     */
    const inspectHolderName = (val: string): JointNameFinding => {
        const trimmed = (val ?? '').trim();
        if (!trimmed) return NONE;

        if (hasDelimiter(trimmed) || hasStandaloneConjunction(trimmed)) {
            return {
                severity: 'flag',
                message: 'This looks like more than one name. Enter the primary account holder only — the person whose identity should be verified.',
            };
        }

        if (hasRunTogetherConjunction(trimmed)) {
            return {
                severity: 'warning',
                message: 'Check this is one person\'s name. If the account has two holders, enter the primary one only.',
            };
        }

        return NONE;
    };

    return { inspectHolderName };
};

export default useJointHolderName;
